import React, { useState } from 'react';
import type { ConnectorCatalog, ConnectorRow } from '../../../types/aai';
import { Action, Chip, EmptyState, RouteMark } from '../../aa';
import { PROVIDER_CATEGORIES } from '../../onboarding/providerCatalog';
import { ManageInWorkspace } from '../ManageInWorkspace';
import { appIconUrl } from '../../dossier/AppIcon';
import { AccessCell } from './AppAccess';

export type PanelMode = 'owner' | 'department' | 'employee';

export interface RowHandlers {
  mode: PanelMode;
  busy: string;
  onConnect: (row: ConnectorRow) => void;
}

const VISUALS: Record<string, { color: string; textColor?: string; initial: string }> = {};
for (const cat of PROVIDER_CATEGORIES) {
  for (const p of cat.providers) VISUALS[p.slug] = { color: p.color, textColor: p.textColor, initial: p.initial };
}

const live = (status: string | null | undefined): boolean => status === 'connected' || status === 'active';

/* MCP servers have their own Settings section. An app the Owner turned off or
   kept never reaches anyone else: the server leaves it out. */
export const rowsOf = (catalog: ConnectorCatalog, mode: PanelMode): ConnectorRow[] => {
  const apps = catalog.connectors.filter((r) => r.kind !== 'mcp');
  if (mode === 'owner') return apps;
  return apps.filter((r) => r.connect_here);
};

export const isYours = (row: ConnectorRow): boolean => {
  if (!row.connect_here) return Boolean(row.workspace_git?.connected);
  return live(row.personal_status) || live(row.organization_status);
};

export const searchText = (row: ConnectorRow): string => `${row.label} ${row.slug} ${row.category_label}`;

const TILE: Record<'md' | 'lg' | 'xl', { box: string; icon: number }> = {
  md: { box: 'h-[34px] w-[34px] text-[12px]', icon: 18 },
  lg: { box: 'h-[37px] w-[37px] text-[13px]', icon: 20 },
  xl: { box: 'h-[46px] w-[46px] text-[15px]', icon: 26 },
};

export const Mark: React.FC<{ row: ConnectorRow; size?: 'md' | 'lg' | 'xl' }> = ({ row, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  const tile = TILE[size];
  const url = row.logo || appIconUrl(row.slug);
  if (url && !failed) {
    return (
      <span className={`${tile.box} grid shrink-0 place-items-center rounded-tile border border-line bg-white`}>
        <img src={url} alt="" width={tile.icon} height={tile.icon} className="object-contain" onError={() => setFailed(true)} />
      </span>
    );
  }
  const v = VISUALS[row.slug];
  if (!v) return <RouteMark glyph={row.label.charAt(0).toUpperCase()} size={size} />;
  return (
    <span aria-hidden className={`${tile.box} grid shrink-0 place-items-center rounded-tile font-bold`} style={{ backgroundColor: v.color, color: v.textColor || '#fff' }}>
      {v.initial}
    </span>
  );
};

export const authOf = (row: ConnectorRow): string => {
  if (!row.connect_here) return row.workspace_git?.connected ? 'Workspace' : 'Not yet';
  if (live(row.personal_status)) return 'Individual';
  if (live(row.organization_status)) return 'Company';
  return 'Not yet';
};

const Done = () => <Chip tone="ok">Connected</Chip>;

export const statusOf = (row: ConnectorRow, catalog: ConnectorCatalog, h: RowHandlers): React.ReactNode => {
  if (!row.enabled) return <Chip tone="warn">Off for everyone</Chip>;
  if (!row.connect_here) {
    if (row.workspace_git?.connected) return <Done />;
    return <ManageInWorkspace row={row} label="Connect" className="!rounded-act !px-2.5 !py-[5px] !text-[12px] hover:!border-line hover:!bg-canvas-inset" />;
  }
  if (live(row.personal_status)) return <Done />;
  if (!catalog.can_use) return <span className="text-ink-soft">Not for your role</span>;
  return <Action size="sm" variant="quiet" disabled={h.busy === row.slug} reason="Connecting" onClick={() => h.onConnect(row)}>Connect</Action>;
};

const HEAD = 'px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted';
const CELL = 'px-3 py-3 text-[13.5px] text-ink';

export const ConnectorsTable: React.FC<{
  rows: ConnectorRow[];
  catalog: ConnectorCatalog;
  handlers: RowHandlers;
  onOpen: (row: ConnectorRow) => void;
  emptyTitle: string;
  emptyReason: string;
  onChanged?: () => void;
}> = ({ rows, catalog, handlers, onOpen, emptyTitle, emptyReason, onChanged = () => undefined }) => {
  if (rows.length === 0) return <EmptyState title={emptyTitle} reason={emptyReason} />;
  const showAccess = handlers.mode === 'owner' && Boolean(catalog.can_switch_company);
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-line">
          <th className={HEAD}>Connector</th>
          <th className={`${HEAD} hidden sm:table-cell`}>Type</th>
          <th className={`${HEAD} hidden md:table-cell`}>Authorization</th>
          {showAccess && <th className={HEAD}>Access</th>}
          <th className={HEAD}>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.slug}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(row)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(row); } }}
            className="cursor-pointer border-b border-line-soft transition-colors hover:bg-canvas-inset focus-visible:bg-canvas-inset focus-visible:outline-none"
          >
            <td className={CELL}>
              <span className="flex items-center gap-3">
                <Mark row={row} />
                <span className="font-medium">{row.label}</span>
              </span>
            </td>
            <td className={`${CELL} hidden text-ink-muted sm:table-cell`}>
              <span className="flex items-center gap-2">{row.category_label}</span>
            </td>
            <td className={`${CELL} hidden text-ink-muted md:table-cell`}>{authOf(row)}</td>
            {showAccess && (
              <td className={`${CELL} whitespace-nowrap`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <AccessCell row={row} onChanged={onChanged} />
              </td>
            )}
            <td className={`${CELL} w-[1%] whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>{statusOf(row, catalog, handlers)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
